import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import { INotePurchaseReport } from '@/interfaces/report';
import { formatDate } from '@/utils/dataUtils';

interface IProps {
  data: INotePurchaseReport[][];
  startDate?: Date | string | Dayjs | null;
}

const ExcelExport = ({ data, startDate }: IProps) => {
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('notePurchaseReport');

    const titleCell = worksheet.getCell('A1');
    titleCell.value = `Note Purchase ${formatDate(dayjs(startDate), 'MMMM YYYY')}`;

    worksheet.addRow({}); // Row 2: Empty row

    // Add headers in Row 3 manually
    worksheet.addRow([
      'No.',
      'Comercial',
      'Invoice Date',
      'Amount',
      'PO#',
      'Div',
      'Invoice',
      'Date',
      'Amount',
      'Product',
      'Product Name',
      'Lot Number',
      'Qty',
      'Price',
      'Amount USD',
      '-',
      'Group',
      'Amount THB',
      'Whole Year',
      'Diff',
      'Freight Amount',
      'Freight per',
      '-',
      'Customs',
      'Convert to',
      '-',
      'Import Rate',
      'Custom Duty',
      'Duty per',
      '-',
      'VAT Rate',
      'VAT Amount',
      'VAT per',
      '-',
      'Certificate No.',
      'Invoice No. Vender',
    ]);
    worksheet.views = [
      {
        state: 'frozen',
        xSplit: 6, // Freezes columns A to F
        ySplit: 3, // Freezes rows 1 to 3
      },
    ];
    // Set column widths (since we’re not using `worksheet.columns`)
    worksheet.columns = [
      { key: 'no', width: 5 },
      { key: 'comercial', width: 15 },
      { key: 'invoice_date', width: 15 },
      { key: 'amount', width: 15 },
      { key: 'po', width: 15 },
      { key: 'div', width: 5 },
      { key: 'invoice_2', width: 15 },
      { key: 'invoice_date2', width: 15 },
      { key: 'amount_2', width: 15 },
      { key: 'product', width: 15 },
      { key: 'product_name', width: 20 },
      { key: 'lot_number', width: 15 },
      { key: 'qty', width: 10 },
      { key: 'price', width: 15 },
      { key: 'amount_usd', width: 15 },
      { key: 'dash_1', width: 5 },
      { key: 'group', width: 15 },
      { key: 'amount_thb', width: 15 },
      { key: 'whole_year', width: 15 },
      { key: 'diff', width: 10 },
      { key: 'freight_amount', width: 15 },
      { key: 'freight_per', width: 15 },
      { key: 'zero_1', width: 5 },
      { key: 'customs', width: 15 },
      { key: 'convert_to', width: 15 },
      { key: 'zero_2', width: 5 },
      { key: 'import_rate', width: 15 },
      { key: 'custom_duty', width: 15 },
      { key: 'duty_per', width: 15 },
      { key: 'zero_3', width: 5 },
      { key: 'vat_rate', width: 15 },
      { key: 'vat_amount', width: 15 },
      { key: 'vat_per', width: 15 },
      { key: 'dash_2', width: 5 },
      { key: 'certificate_no', width: 20 },
      { key: 'invoice_no_vender', width: 20 },
    ];

    // Populate data starting from row 4
    data?.forEach((a, idx) => {
      a.forEach(c => {
        const row = worksheet.addRow({
          no: idx + 1,
          comercial: c.invNO,
          invoice_2: c.invNO,
          invoice_date: formatDate(c.invDate),
          invoice_date2: formatDate(c.invDate),
          amount: c.invAmount,
          amount_2: c.invAmount,
          po: c.poNo,
          div: c.businessUnitName,
          product: c.productCode,
          product_name: c.productName,
          lot_number: `${c.lotNumber}`,
          qty: c.quantity,
          price: c.unitCost,
          amount_usd: c.amountUSD,
          group: c.rate,
          amount_thb: c.amountTHB,
          freight_amount: c.freightAmount,
          freight_per: c.freight,
          import_rate: c.dutyRate,
          custom_duty: c.dutyAmount,
          duty_per: c.duty,
          vat_rate: c.vatRate,
          vat_amount: c.vatAmount,
          vat_per: c.vatCost,
          certificate_no: c.decNO,
          invoice_no_vender: c.billingNo,
        });
        const rowIndex = row.number; // Get row index in the worksheet
        [
          'D',
          'I',
          'M',
          'N',
          'O',
          'Q',
          'R',
          'U',
          'AA',
          'AB',
          'AE',
          'AF',
        ].forEach(col => {
          const cell = worksheet.getCell(`${col}${rowIndex}`);
          cell.numFmt = '#,##0.00';
        });

        ['V', 'AC', 'AG'].forEach(col => {
          const cell = worksheet.getCell(`${col}${rowIndex}`);
          cell.numFmt = '#,##0.00';
        });
      });
      const row = worksheet.addRow({
        no: '',
        comercial: '',
        invoice_2: '',
        invoice_date: '',
        invoice_date2: '',
        amount: '',
        amount_2: '',
        po: '',
        div: '',
        product: '',
        product_name: '',
        lot_number: '',
        qty: '',
        price: '',
        amount_usd: 0,
        group: '',
        amount_thb: '',
        freight_amount: 0,
        freight_per: '',
        import_rate: '',
        custom_duty: 0,
        duty_per: '',
        vat_rate: '',
        vat_amount: 0,
        vat_per: '',
        certificate_no: '',
        invoice_no_vender: '',
      }); // Empty row for spacing
      const rowIndex = row.number; // Get row index in the worksheet
      worksheet.getCell(`O${rowIndex}`).value = {
        formula: `SUM(O${rowIndex - a.length}:O${rowIndex - 1})`, // Example: Calculate VAT as 7% of totalAmount
      };
      worksheet.getCell(`U${rowIndex}`).value = {
        formula: `SUM(U${rowIndex - a.length}:U${rowIndex - 1})`, // Example: Calculate VAT as 7% of totalAmount
      };
      worksheet.getCell(`AB${rowIndex}`).value = {
        formula: `SUM(AB${rowIndex - a.length}:AB${rowIndex - 1})`, // Example: Calculate VAT as 7% of totalAmount
      };
      worksheet.getCell(`AF${rowIndex}`).value = {
        formula: `SUM(AF${rowIndex - a.length}:AF${rowIndex - 1})`, // Example: Calculate VAT as 7% of totalAmount
      };
      ['O', 'U', 'AB', 'AF'].forEach(col => {
        const cell = worksheet.getCell(`${col}${rowIndex}`);
        cell.border = {
          top: { style: 'thin' }, // Thin top border
          bottom: { style: 'double' }, // Double bottom border
        };
        cell.numFmt = '#,##0.00';
      });

      worksheet.getCell(`A${rowIndex}`).numFmt = '#,##0.00';
      worksheet.addRow([]); // Empty row for spacing
      worksheet.addRow([]); // Empty row for spacing
    });

    // Apply background color to all Column B from row 3 onwards
    const lastRow = worksheet.rowCount; // Get total row count
    for (let i = 3; i <= lastRow; i++) {
      worksheet.getCell(`B${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`C${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`D${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`F${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF80FF' }, // Yellow background
      };
      worksheet.getCell(`G${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`H${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`I${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EAEC' }, // Yellow background
      };
      worksheet.getCell(`J${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F1DDCC' }, // Yellow background
      };
      worksheet.getCell(`K${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F1DDCC' }, // Yellow background
      };
      worksheet.getCell(`M${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F1DDCC' }, // Yellow background
      };
      worksheet.getCell(`Q${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E3E19A' }, // Yellow background
      };
      worksheet.getCell(`R${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E3E19A' }, // Yellow background
      };
      worksheet.getCell(`U${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D0D2FF' }, // Yellow background
      };
      worksheet.getCell(`V${i}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'C2B280' }, // Yellow background
      };
    }

    workbook.calcProperties.fullCalcOnLoad = true;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `note-purchase-report-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div>
      <Button type="primary" ghost onClick={handleExport}>
        <ExportOutlined /> Export to Excel
      </Button>
    </div>
  );
};

export default ExcelExport;
